/**
 * Meeting Section Redux Slice
 * Manages meeting sections and preparation answers
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { MeetingSectionState } from "@/types/meeting-section.types";
import { fetchMeetingSections } from "@/services/api/endpoints/meeting-section.api";
import {
  savePreparationData,
  loadPreparationData,
} from "@/utils/sessionStorageManager";

const initialState: MeetingSectionState = {
  sections: [],
  loading: false,
  error: null,
  preparationAnswers: {},
  currentModuleId: null,
};

/**
 * Fetch meeting sections by module ID
 */
export const getMeetingSections = createAsyncThunk(
  "meetingSection/fetchSections",
  async (moduleId: string, { rejectWithValue }) => {
    try {
      const data = await fetchMeetingSections(moduleId);
      return { data, moduleId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch meeting sections"
      );
    }
  }
);

const meetingSectionSlice = createSlice({
  name: "meetingSection",
  initialState,
  reducers: {
    // Save a preparation answer and sync with sessionStorage
    savePreparationAnswer: (
      state,
      action: PayloadAction<{ complementId: string; answer: string }>
    ) => {
      state.preparationAnswers[action.payload.complementId] =
        action.payload.answer;

      // Sync with sessionStorage
      if (state.currentModuleId) {
        const totalQuestions = state.sections.reduce(
          (total, section) => total + section.complements.length,
          0
        );

        // Build questions map from sections
        const questions: { [complementId: string]: string } = {};
        state.sections.forEach((section) => {
          section.complements.forEach((complement) => {
            questions[complement.id] = complement.complement;
          });
        });

        savePreparationData({
          answers: state.preparationAnswers,
          questions,
          moduleId: state.currentModuleId,
          timestamp: Date.now(),
          totalQuestions,
        });
      }
    },

    // Load preparation answers from sessionStorage
    loadPreparationAnswers: (
      state,
      action: PayloadAction<{ [complementId: string]: string }>
    ) => {
      state.preparationAnswers = action.payload;
    },

    // Clear all preparation answers (Redux + sessionStorage)
    clearPreparationAnswers: (state) => {
      state.preparationAnswers = {};
    },

    // Clear sections (when leaving the meeting)
    clearSections: (state) => {
      state.sections = [];
      state.currentModuleId = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getMeetingSections - pending
      .addCase(getMeetingSections.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // getMeetingSections - fulfilled
      .addCase(getMeetingSections.fulfilled, (state, action) => {
        state.loading = false;
        state.sections = action.payload.data;
        state.currentModuleId = action.payload.moduleId;
        // Clear previous answers when loading new sections
        state.preparationAnswers = {};
      })
      // getMeetingSections - rejected
      .addCase(getMeetingSections.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  savePreparationAnswer,
  loadPreparationAnswers,
  clearPreparationAnswers,
  clearSections,
} = meetingSectionSlice.actions;

export default meetingSectionSlice.reducer;
